import Dexie from 'dexie';
import { createXYZ } from 'ol/tilegrid';
import { AuthService } from '../services/auth/auth-service'
import { faro } from '../utils/rum-utility';

export enum DownloadState {
    IDLE = 'idle',
    PENDING = 'pending',
    DONE = 'done',
    ERROR = 'error'
}

export interface DownloadProgress {
    totalTiles: number;
    downloadedTiles: number;
    currentZoom: number;
    percentage: number;
}

export class cloudMapsOffline extends Dexie {
    _db!: Dexie.Table<{ url: string; tile: Blob }, string>;
    private _downloadState: DownloadState = DownloadState.IDLE;
    private _downloadProgress: DownloadProgress = {
    totalTiles: 0,
    downloadedTiles: 0,
    currentZoom: 0,
    percentage: 0
    };

    private _onProgressChange: ((progress: DownloadProgress) => void) | null = null;
    private _onStateChange: ((state: DownloadState) => void) | null = null;
    private _totalSize: number = 0;

    constructor() {
        super('MapsDB')
        const mapsDbVersion = (<any>window)["appenv"].indexedDBConfig?.mapsDbVersion ?? 1; 
        this.version(mapsDbVersion).stores({
            tiles: 'url', 
        });
        this._db = this.table('tiles');
        this.open(); //initialise first
    }

    get downloadState(): DownloadState {
        return this._downloadState;
    }
    
    get downloadProgress(): DownloadProgress {
        return this._downloadProgress;
    }
    
    setProgressCallback(callback: (progress: DownloadProgress) => void): void {
        this._onProgressChange = callback;
    }
    
    setStateCallback(callback: (state: DownloadState) => void): void {
        this._onStateChange = callback;
    }
    // common utility : To hold download state - OSM & ENC
    private _updateDownloadState(state: DownloadState): void {
        this._downloadState = state;
        if (this._onStateChange) {
            this._onStateChange(state);
        }
    }
    // common utility : To follow download progress  - OSM & ENC
    private _updateDownloadProgress(progress: Partial<DownloadProgress>): void {
        this._downloadProgress = { ...this._downloadProgress, ...progress };
        
        // calculating percentage if we have total tiles
        if (this._downloadProgress.totalTiles > 0) {
            this._downloadProgress.percentage = Math.floor(
                (this._downloadProgress.downloadedTiles / this._downloadProgress.totalTiles) * 100
            );
        }
        
        if (this._onProgressChange) {
            this._onProgressChange(this._downloadProgress);
        }
    }
    // common utility : To cancel download - OSM & ENC
    cancelDownload(): void {
        if (this._downloadState === DownloadState.PENDING) {
            this._updateDownloadState(DownloadState.IDLE);
        }
    }

    // utility : Download whole set of tiles - OSM world map
    async downloadWorldTiles(zoom: number = 4) {
        await this.open();

        const TILE_SERVER_URL = "https://tile.openstreetmap.de/{z}/{x}/{y}.png";
    
        const tileCount = Math.pow(2, zoom); // Number of tiles per row/column (16 for zoom 4)

        if (this._downloadState === DownloadState.PENDING) {
            return;
        }
        this._updateDownloadState(DownloadState.PENDING);
        
        try {
            console.log(`Downloading all tiles for zoom ${zoom} (${tileCount}x${tileCount} tiles)`);

            const totalTilesToDownload = Math.pow(tileCount, 2);

            this._updateDownloadProgress({
                totalTiles: totalTilesToDownload,
                downloadedTiles: 0,
                currentZoom: zoom,
                percentage: 0
            });
            let downloadedCount = 0;

            for (let x = 0; x < tileCount; x++) {
                for (let y = 0; y < tileCount; y++) {
                    // checking if download was cancelled
                    if ((this._downloadState as DownloadState) !== DownloadState.PENDING) {
                        console.log('Download cancelled');
                        return;
                    }
                    const tileUrl = TILE_SERVER_URL.replace("{z}", zoom?.toString()).replace("{x}", x?.toString()).replace("{y}", y?.toString());
                    await this._downloadTile(tileUrl, `tile_${zoom}_${x}_${y}.png`);
                    // Update progress
                    downloadedCount ++;
                    this._updateDownloadProgress({
                        downloadedTiles: downloadedCount
                    });
                }
            }
            console.log('Finished downloading all world map tiles', downloadedCount);
            console.log(`Total size: ${this._totalSize} bytes`);
            // RUM event to track offline map tiles download completion
            faro.api.pushEvent('map-offline-download', { size: (parseFloat((this._totalSize / (1024 * 1024)).toFixed(2))) });
            this._updateDownloadState(DownloadState.DONE);
        } catch (err) {
            console.error('Error during background world map tile download:', err);
            this._updateDownloadState(DownloadState.ERROR);
        }
    }
    // utility : Download each tile - OSM world map
    async _downloadTile(url: string, filename: string) {
        try {
            const cachedTiles = await this
                    .table('tiles')
                    .where('url')
                    .equals(url)
                    .count();
                    
            if (cachedTiles > 0) return null;

            const response = await fetch(url);
            if (!response.ok) {
                console.error(`Failed to download ${url}`);
                return;
            }
    
            const blob = await response.blob();
            this._totalSize += blob.size; // Add size to total

            // Save to cache
            await this.table('tiles').put({
                url,
                tile: blob,
            })
        } catch (error) {
            console.error(`Error downloading tile ${url}:`, error);
        }
    }

    // utility : Download whole set of tiles - ENC maps 
    async downloadEncTiles() {
        const {
            vesselOnCloud: isVesselOnCloud = false,
            services: {
                encTileServer: _ENCTILESERVER = '',
                mapTileServer: _MAPTILESERVER = '',
            },
            scope = '',
            consumerKey = '',
            xRequestor = '',
        } = (window as any).appenv;

        const MAP_CONFIG: {
            extent: [number, number, number, number]
            tileSize: [number, number]
        } = {
            extent:  [
                -20037508.342789244, -20037508.342789244, 20037508.342789244,
                20037508.342789244,
            ],
            tileSize: [256, 256],
        }
        
        const OFFLINE_ZOOM_CONFIG : {
            minZoom: number
            maxZoom: number
        } = {
            minZoom: 4,
            maxZoom: 5,
        }
        const depths = {
            safetyContour: 20,
            shallowContour: 15,
            deepContour: 30,
            safetyDepth: 20,
        };
        const colorScheme: string = 'day';

        if (this._downloadState === DownloadState.PENDING) {
            return;
        }
        
        this._updateDownloadState(DownloadState.PENDING);
        
        try {
            // Use provided options or defaults
            const minZoom = OFFLINE_ZOOM_CONFIG.minZoom;
            const maxZoom = OFFLINE_ZOOM_CONFIG.maxZoom;
            const extent = MAP_CONFIG.extent;
            const tileSize = MAP_CONFIG.tileSize;
    
            // Dynamically create tile grids based on zoom range
            const mapTileGrid = createXYZ({
                extent,
                tileSize,
                minZoom,
                maxZoom,
            });
    
            // calculating total number of tiles to download
            let totalTileCount = 0;
            const allTileCoords: [number, number, number][][] = [];
            
            for (let zoom = minZoom; zoom <= maxZoom; zoom++) {
                const zoomTileCoords: [number, number, number][] = [];
                
                mapTileGrid.forEachTileCoord(
                    extent,
                    zoom,
                    function (tileCoord) {
                        zoomTileCoords.push([tileCoord[0], tileCoord[1], tileCoord[2]]);
                    }
                );
                
                allTileCoords.push(zoomTileCoords);
                totalTileCount += zoomTileCoords.length;
            }
            
            this._updateDownloadProgress({
                totalTiles: totalTileCount,
                downloadedTiles: 0,
                currentZoom: minZoom,
                percentage: 0
            });
    
            const downloadTile = async (tileCoord: [number, number, number]) => {
                // extract zoom, x, and y from tile coordinate
                const [z, x, y] = tileCoord;
                
                // y coordinate needs to be inverted for the URL template
                // we need to handle this manually rather than relying on the URL template
                const invertedY = (1 << z) - y - 1;
                
                // manually building the URL with the correct components
                const urlBase = `${_MAPTILESERVER.baseurl}/map/${z}/${x}/${invertedY}.png`;
                
                const queryParams = new URLSearchParams({
                    safetyContour: depths.safetyContour.toString(),
                    safetyDepth: depths.safetyDepth.toString(),
                    shallowContour: depths.shallowContour.toString(),
                    deepContour: depths.deepContour.toString(),
                    beauty: 'true',
                    simplified: 'false',
                    levelOfDetail: '2',
                    coverage: 'false',
                    palette: colorScheme,
                });
    
                const fullUrl = `${urlBase}?${queryParams}`;
                
                const cachedTiles = await this
                    .table('tiles')
                    .where('url')
                    .equals(fullUrl)
                    .count();
                    
                if (cachedTiles > 0) return null;
    
                let fetchOptions = {};
                if (isVesselOnCloud) {
                    const accessToken = await AuthService.getAccessToken(scope);
                    fetchOptions = {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'consumer-key': consumerKey,
                            'X-Requestor': xRequestor,
                        },
                    };
                }
    
                try {
                    const response = await fetch(fullUrl, fetchOptions);
                    if (!response.ok) {
                        throw new Error(
                            `HTTP error! status: ${response.status}`
                        );
                    }

                    const blob = await response.blob();
                    this._totalSize += blob.size; // Add size to total
    
                    return {
                        url: fullUrl,
                        tile: blob,
                    };
                } catch (error) {
                    console.error(`Error downloading tile ${fullUrl}:`, error);
                    return null;
                }
            };
    
            const downloadBatchSize = 100;
            let downloadedCount = 0;
    
            // processing each zoom level
            for (let zoomIndex = 0; zoomIndex < allTileCoords.length; zoomIndex++) {
                const zoom = minZoom + zoomIndex;
                const tileCoords = allTileCoords[zoomIndex];
                
                // updating current zoom level in progress
                this._updateDownloadProgress({
                    currentZoom: zoom
                });
    
                console.log(
                    `Starting download of ${tileCoords.length} tiles at zoom level ${zoom}`
                );
                
                for (let i = 0; i < tileCoords.length; i += downloadBatchSize) {
                    // checking if download was cancelled
                    if ((this._downloadState as DownloadState) !== DownloadState.PENDING) {
                        console.log('Download cancelled');
                        return;
                    }
    
                    const tileCoordsBatch = tileCoords.slice(
                        i,
                        i + downloadBatchSize,
                    );
                    
                    const tiles = (
                        await Promise.all(tileCoordsBatch.map(downloadTile))
                    ).filter((x) => x != null);
                    
                    if (tiles.length > 0) {
                        await this.table('tiles').bulkPut(tiles);
                    }
                    
                    // Update progress
                    downloadedCount += tileCoordsBatch.length;
                    this._updateDownloadProgress({
                        downloadedTiles: downloadedCount
                    });
    
                    console.log(
                        `Processed ${i + tileCoordsBatch.length} of ${
                            tileCoords.length
                        } tiles at zoom level ${zoom} (${
                            this._downloadProgress.percentage
                        }% complete)`
                    );
                }

                console.log(`Total size: ${this._totalSize} bytes`);
            }
    
            console.log('Finished downloading all map tiles');
            this._updateDownloadState(DownloadState.DONE);
        } catch (error) {
            console.error('Error during background tile download:', error);
            this._updateDownloadState(DownloadState.ERROR);
        }
    }
}

export const offlineDb = new cloudMapsOffline();
